import { Grid } from '@mui/material'
import axios from 'axios'
import dayjs from 'dayjs'
import { saveAs } from 'file-saver'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector, useNotification } from '../../store/redux/hooks'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import { getSelectedDate, monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { setPageName } from '../../store/redux/reducers/pageNameSlice'
import { handleGetWorklogZip } from '../../store/redux/reducers/reportsSliceAxios'
import EmployeeMonthlyHoursSummary from './EmployeeMonthlyHoursSummary'
import MasonryTileImportExport from './MasonryTileImportExport'
import MasonryTileNotifications from './MasonryTileNotifications'

const GeneralSettingsView: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { displayNotification } = useNotification()
  const selectedDate = useAppSelector(getSelectedDate)
  const [isLoading, setIsLoading] = React.useState(false)

  useEffect(() => {
    dispatch(setPageName(t('page.name.generalSettings')))
    dispatch(pageHeaderActions.setButtons({ yearPicker: false }))
    dispatch(monthPickerActions.setDate(dayjs().toString()))
    dispatch(breadcrumbsItemsActions.setSelectedItems({ adminSettings: t('page.name.adminSettings.general') }))
    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t])

  useEffect(() => {
    if (!selectedDate) {
      dispatch(monthPickerActions.setDate(dayjs().toString()))
    }
  }, [dispatch, selectedDate])

  const handleDownloadWorklogZipReport = async () => {
    try {
      setIsLoading(true)
      const response = await handleGetWorklogZip(dayjs(selectedDate).year())
      const blob = new Blob([response.data], { type: response.headers['content-type'] as string | undefined })
      const contentDisposition = response.headers['content-disposition']

      if (contentDisposition) {
        let fileName = contentDisposition.split('filename*=')[1]

        if (fileName) {
          fileName = decodeURIComponent(fileName.split("''")[1])
        } else {
          fileName = contentDisposition.split('filename=')[1].replace(/^"(.*)"$/, '$1')
          fileName = decodeURIComponent(fileName)
        }

        saveAs(blob, fileName)
      } else {
        saveAs(blob, 'report')
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const contentType = error.response.headers['content-type'] as string | undefined
        if (contentType && contentType.includes('application/json')) {
          const reader = new FileReader()
          reader.onloadend = () => {
            const jsonData = JSON.parse(reader.result as string)
            if (jsonData.error) {
              let message = ''
              Object.values(jsonData.error).forEach(errorMessage => {
                message += errorMessage + ' '
              })
              displayNotification({ message: message.trim(), type: 'error' })
            } else {
              displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
            }
          }

          reader.readAsText(error.response.data)
        } else {
          displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={6}>
        <MasonryTileImportExport
          isLoading={isLoading}
          handleDownloadWorklogZipReport={handleDownloadWorklogZipReport}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <MasonryTileNotifications />
      </Grid>

      <Grid item xs={12} pb={2}>
        <EmployeeMonthlyHoursSummary />
      </Grid>
    </Grid>
  )
}

export default GeneralSettingsView
